import React, { useState, useRef, useEffect } from "react";
import type { Batch } from "./LMBuilder/types";
import ApiService from "../_services/ApiService";

interface BatchMultiSelectProps {
  selectedBatches: Batch[];
  onChange: (batches: Batch[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const BatchMultiSelect: React.FC<BatchMultiSelectProps> = ({
  selectedBatches = [],
  onChange,
  placeholder = "Search and select batches...",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Batch[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Search batches with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await ApiService.searchBatches(searchQuery);
        const responseData = response.data as any;
        const batches = responseData?.data || responseData || [];
        setSearchResults(Array.isArray(batches) ? batches : []);
      } catch (error) {
        console.error('[BatchMultiSelect] Error searching batches:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const isBatchSelected = (batchId: number) =>
    selectedBatches.some((b) => b.id === batchId);

  const toggleBatch = (batch: Batch) => {
    if (isBatchSelected(batch.id)) {
      onChange(selectedBatches.filter((b) => b.id !== batch.id));
    } else {
      onChange([...selectedBatches, batch]);
    }
    setSearchQuery(""); // Clear search after selection
  };

  const removeBatch = (batchId: number) => {
    onChange(selectedBatches.filter((b) => b.id !== batchId));
  };

  const clearAll = () => {
    onChange([]);
  };

  const getBatchDisplayName = (batch: Batch) => {
    return batch.displayName || batch.name || batch.batchCode || `Batch ${batch.id}`;
  };

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="tag-multi-select" ref={dropdownRef}>
      {/* Unified container with chips and inline search input */}
      <div
        className="tag-multi-select-container"
        onClick={() => !disabled && inputRef.current?.focus()}
        style={{ cursor: disabled ? 'default' : 'text' }}
      >
        <div className="tag-chips-container">
          {selectedBatches.map((batch) => (
            <span key={batch.id} className="tag-chip">
              {getBatchDisplayName(batch)}
              {!disabled && (
                <button
                  type="button"
                  className="tag-chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeBatch(batch.id);
                  }}
                  aria-label="Remove batch"
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {/* Inline search input */}
          {!disabled && (
            <input
              ref={inputRef}
              type="text"
              className="tag-inline-input"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={selectedBatches.length === 0 ? placeholder : ""}
              disabled={disabled}
              style={{ flex: 1, minWidth: '120px', border: 'none', outline: 'none' }}
            />
          )}
        </div>
        {!disabled && selectedBatches.length > 0 && (
          <button
            type="button"
            className="tag-clear-all"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            aria-label="Clear all"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Dropdown Options */}
      {isOpen && !disabled && (
        <div className="tag-dropdown">
          {isSearching ? (
            <div className="tag-dropdown-item tag-dropdown-empty">
              Searching...
            </div>
          ) : searchQuery.trim().length < 2 ? (
            <div className="tag-dropdown-item tag-dropdown-empty">
              Type at least 2 characters to search
            </div>
          ) : searchResults.length === 0 ? (
            <div className="tag-dropdown-item tag-dropdown-empty">
              No batches found for "{searchQuery}"
            </div>
          ) : (
            searchResults.map((batch) => (
              <div
                key={batch.id}
                className={`tag-dropdown-item ${
                  isBatchSelected(batch.id) ? "tag-selected" : ""
                }`}
                onClick={() => toggleBatch(batch)}
              >
                <input
                  type="checkbox"
                  checked={isBatchSelected(batch.id)}
                  readOnly
                  className="tag-checkbox"
                />
                <span className="tag-name">{getBatchDisplayName(batch)}</span>
                {batch.batchCode && batch.batchCode !== batch.displayName && (
                  <span className="tag-type">({batch.batchCode})</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default BatchMultiSelect;
